import { useEffect, useRef } from "react";
import { UndoRedoControls } from "../reusable/UndoRedoControls";
import {
    Brush,
    Droplet,
    FileOutput,
    Frame,
    Pencil,
    PaintBucket,
    Pipette,
    Eraser,
    Share2,
    Square,
    Circle,
    Minus,
} from "lucide-react";
import {
    GRID_SIZE_OPTIONS,
    usePixelSvgCanvas,
    type GridSize,
    type ToolKind,
} from "./PixelSvgCanvasContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import type { CellShape, OutputMode } from "../../utils/pixelSvgRender";

const TOOLS: { id: ToolKind; label: string; Icon: typeof Pencil; hotkey: string }[] = [
    { id: "pencil", label: "Pencil", Icon: Pencil, hotkey: "B" },
    { id: "eraser", label: "Eraser", Icon: Eraser, hotkey: "E" },
    { id: "bucket", label: "Bucket fill", Icon: PaintBucket, hotkey: "G" },
    { id: "rect", label: "Rectangle", Icon: Square, hotkey: "R" },
    { id: "ellipse", label: "Ellipse", Icon: Circle, hotkey: "O" },
    { id: "line", label: "Line", Icon: Minus, hotkey: "L" },
    { id: "eyedropper", label: "Eyedropper", Icon: Pipette, hotkey: "I" },
];

const CELL_SHAPES: { id: CellShape; label: string; description: string }[] = [
    {
        id: "square",
        label: "Square",
        description:
            "Sharp axis-aligned rects. Greedy merge and per-color paths reduce element count.",
    },
    {
        id: "rounded",
        label: "Rounded",
        description:
            "Each filled cell exports as a rounded rect (~35% corner radius). Merge uses one shape per cell.",
    },
    {
        id: "circle",
        label: "Circle",
        description:
            "Each filled cell exports as a circle inscribed in the cell. Merge uses one shape per cell.",
    },
];

const OUTPUT_MODES: { id: OutputMode; label: string; description: string }[] = [
    {
        id: "raw",
        label: "Raw rects",
        description: "One <rect> per filled cell. Largest output.",
    },
    {
        id: "greedy-rects",
        label: "Greedy rects",
        description:
            "Adjacent same-color cells merged into the largest possible rectangles.",
    },
    {
        id: "paths",
        label: "Per-color path",
        description:
            "One <path> per unique color, built from greedy rectangles. Smallest file.",
    },
];

export function PixelSvgCanvasControls() {
    const {
        tool,
        setTool,
        brushSize,
        setBrushSize,
        color,
        setColor,
        recentColors,
        outputMode,
        setOutputMode,
        outputSize,
        setOutputSize,
        cellShape,
        setCellShape,
        showOutputPane,
        setShowOutputPane,
        editorWhiteBackground,
        setEditorWhiteBackground,
        downloadSvg,
        buildExportSvgString,
        gridSize,
        setGridSize,
        hasContent,
        clearAll,
        undo,
        redo,
        canUndo,
        canRedo,
        resetParameters,
    } = usePixelSvgCanvas();

    const colorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        return () => {
            if (colorTimeoutRef.current) clearTimeout(colorTimeoutRef.current);
        };
    }, []);

    const handleColorPickerChange = (next: string) => {
        if (colorTimeoutRef.current) clearTimeout(colorTimeoutRef.current);
        colorTimeoutRef.current = setTimeout(() => {
            setColor(next);
        }, 200);
    };

    const handleClear = () => {
        if (!hasContent) return;
        clearAll();
    };

    const handleGridSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const n = Number(e.target.value);
        if (!GRID_SIZE_OPTIONS.includes(n as GridSize)) return;
        if (hasContent) {
            const ok = window.confirm(
                `Changing the grid size from ${gridSize}x${gridSize} to ${n}x${n} clears the canvas. Continue?`,
            );
            if (!ok) return;
        }
        setGridSize(n as GridSize);
    };

    return (
        <div className="panel-controls pixel-svg-canvas-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Pixel SVG Canvas</h2>
                    <ToolDescriptionPopover dialogLabel="Pixel SVG Canvas tool description">
                        <p>
                            Paint on a pixel grid where every filled cell is exported
                            as an SVG rectangle. Pick a tool, choose a color, and
                            draw. The viewer can show a live SVG preview; turn off{" "}
                            <strong>Show output preview</strong> below if you only
                            need the canvas.
                        </p>
                        <p>
                            Use the <strong>Output mode</strong> toggle to merge
                            adjacent same-color cells into greedy rectangles or a
                            single path per color, dramatically shrinking the file
                            size for solid regions.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={downloadSvg}
                        disabled={!hasContent}
                        title="Download the rendered SVG (same as Output pane)"
                    >
                        Download SVG
                    </button>
                    <UndoRedoControls
                        canUndo={canUndo}
                        canRedo={canRedo}
                        onUndo={undo}
                        onRedo={redo}
                    />
                    <button
                        type="button"
                        className="generic-button"
                        onClick={handleClear}
                        disabled={!hasContent}
                    >
                        Clear canvas
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <CollapsibleToolGroup title="Canvas" icon={Frame}>
                    <div className="field-row">
                        <label className="field-label">
                            Grid size
                            <select
                                value={gridSize}
                                onChange={handleGridSizeChange}
                                className="anchor-select"
                            >
                                {GRID_SIZE_OPTIONS.map((n) => (
                                    <option key={n} value={n}>
                                        {n} x {n}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <p className="control-panel-hint">
                        Changing the grid size clears the current canvas.
                    </p>
                    <div className="field-row field-row--switch">
                        <CheckboxField
                            checked={editorWhiteBackground}
                            onChange={setEditorWhiteBackground}
                            label="White background in editor"
                            inline
                            title="Shows a solid white area behind the grid in the editor only. Transparent pixels are not affected; exports do not include this background."
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Brush" icon={Brush}>
                    <div className="field-row">
                        <label className="field-label">Tool</label>
                    </div>
                    <div className="pixel-svg-canvas-tool-grid">
                        {TOOLS.map(({ id, label, Icon, hotkey }) => (
                            <button
                                key={id}
                                type="button"
                                className={`pixel-svg-canvas-tool-btn ${tool === id ? "pixel-svg-canvas-tool-btn--active" : ""}`}
                                onClick={() => setTool(id)}
                                title={`${label} (${hotkey})`}
                                aria-pressed={tool === id}
                            >
                                <Icon size={18} strokeWidth={2} aria-hidden />
                                <span className="pixel-svg-canvas-tool-btn-label">
                                    {label}
                                </span>
                            </button>
                        ))}
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Brush size: {brushSize}
                            <input
                                type="range"
                                min={1}
                                max={32}
                                step={1}
                                value={brushSize}
                                onChange={(e) => setBrushSize(Number(e.target.value))}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Color" icon={Droplet}>
                    <div className="field-row pixel-svg-canvas-color-row">
                        <label className="field-label-inline">
                            <span>Current color</span>
                            <input
                                type="color"
                                value={color}
                                onChange={(e) =>
                                    handleColorPickerChange(e.target.value)
                                }
                                className="pixel-svg-canvas-color-input"
                            />
                            <input
                                type="text"
                                value={color}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
                                        setColor(v);
                                    }
                                }}
                                className="pixel-svg-canvas-color-text"
                                spellCheck={false}
                            />
                        </label>
                    </div>
                    {recentColors.length > 0 && (
                        <>
                            <p className="field-label">Recent colors</p>
                            <div className="pixel-svg-canvas-recent">
                                {recentColors.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        className={`pixel-svg-canvas-swatch ${c.toLowerCase() === color.toLowerCase() ? "pixel-svg-canvas-swatch--active" : ""}`}
                                        style={{ background: c }}
                                        onClick={() => setColor(c)}
                                        title={c}
                                        aria-label={`Use ${c}`}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Output" icon={FileOutput}>
                    <div className="field-row field-row--switch">
                        <CheckboxField
                            checked={showOutputPane}
                            onChange={setShowOutputPane}
                            label="Show output preview"
                            inline
                            title="Hide the right-hand SVG preview to save space and CPU; download still works from the header."
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Output size (px)
                            <input
                                type="number"
                                min={1}
                                max={8192}
                                step={1}
                                value={outputSize}
                                onChange={(e) =>
                                    setOutputSize(Number(e.target.value))
                                }
                            />
                        </label>
                    </div>
                    <p className="control-panel-hint">
                        Exports at {outputSize} x {outputSize} while preserving
                        the {gridSize} x {gridSize} grid as the SVG viewBox.
                    </p>
                    <div className="field-row">
                        <label className="field-label">Output mode</label>
                    </div>
                    <div className="pixel-svg-canvas-output-modes">
                        {OUTPUT_MODES.map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                className={`pixel-svg-canvas-mode-btn ${outputMode === m.id ? "pixel-svg-canvas-mode-btn--active" : ""}`}
                                onClick={() => setOutputMode(m.id)}
                                aria-pressed={outputMode === m.id}
                                title={m.description}
                            >
                                <span className="pixel-svg-canvas-mode-btn-label">
                                    {m.label}
                                </span>
                                <span className="pixel-svg-canvas-mode-btn-desc">
                                    {m.description}
                                </span>
                            </button>
                        ))}
                    </div>
                    <div className="field-row">
                        <label className="field-label">Cell shape (export)</label>
                    </div>
                    <p className="control-panel-hint">
                        Editor stays a square grid; this only changes the SVG preview and
                        download.
                    </p>
                    <div className="pixel-svg-canvas-output-modes">
                        {CELL_SHAPES.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                className={`pixel-svg-canvas-mode-btn ${cellShape === s.id ? "pixel-svg-canvas-mode-btn--active" : ""}`}
                                onClick={() => setCellShape(s.id)}
                                aria-pressed={cellShape === s.id}
                                title={s.description}
                            >
                                <span className="pixel-svg-canvas-mode-btn-label">
                                    {s.label}
                                </span>
                                <span className="pixel-svg-canvas-mode-btn-desc">
                                    {s.description}
                                </span>
                            </button>
                        ))}
                    </div>
                </CollapsibleToolGroup>
                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!hasContent}
                        getSvg={() => ({
                            name: "pixel-svg-canvas-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["pixel-svg-canvas"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
